<template>
  <page-template :is-loading="isLoading" :data-exists="selectedFlowControlPolicyData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('flowControlPolicy.configPage.newPageHeadline')
          : $t('flowControlPolicy.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Flow Control Policies List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedFlowControlPolicy && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name] as [string, string])"
          :disabled="isLoading"
          v-model:value="selectedFlowControlPolicy"
          @update:value="switchFlowControlPolicy($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('flowControlPolicy.configPage.switchTooltip')"
          classes="switch-flow-control-policy switch-page-entry"
          data-qa="switch-flow-control-policy"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedFlowControlPolicy && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateFlowControlPolicy()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadFlowControlPolicy()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteFlowControlPolicyModal()"
          class="delete-button"
          :disabled="isLoading || selectedFlowControlPolicyProtected"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isFlowControlPolicyValid"
        :tooltip="saveTooltip"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('flowControlPolicy.configPage.name')"
              :placeholder="$t('flowControlPolicy.configPage.name')"
              :disabled="readOnly"
              v-model:inputValue="selectedFlowControlPolicyData.name"
            />
          </div>
          <div class="rbz-field rbz-field-toggle active-field">
            <rbz-form-toggle
              v-model:buttonValue="selectedFlowControlPolicyData.active"
              :label="$t('flowControlPolicy.configPage.active')"
              :disabled="readOnly"
              data-qa="active-toggle"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('flowControlPolicy.configPage.description')"
              :disabled="readOnly"
              v-model:inputValue="selectedFlowControlPolicyData.description"
              rows="2"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="timeframe"
              data-qa="timeframe-input"
              classes="timeframe"
              type="number"
              number-type="whole"
              :label="$t('flowControlPolicy.configPage.timeFrame')"
              :placeholder="$t('flowControlPolicy.configPage.timeFrame')"
              suffix="seconds"
              :disabled="readOnly"
              :is-invalid="!isTimeframeValid"
              :error-message-text="$t('flowControlPolicy.configPage.timeFrameInvalid')"
              v-model:inputValue="selectedFlowControlPolicyData.timeframe"
            />
          </div>
          <div class="rbz-field tags-selection-field">
            <tags-selection
              :value="selectedFlowControlPolicyData.tags"
              @update:value="selectedFlowControlPolicyData.tags = $event as string[]"
              :label="$t('flowControlPolicy.configPage.tags')"
              :disabled="readOnly"
            />
            <div class="automatic-tags">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
          <div class="rbz-field count-by-field" :class="{ 'is-active': countByFieldActive }">
            <label class="rbz-label">
              {{ $t('flowControlPolicy.configPage.countBy') }}
            </label>
            <key-option
              v-for="(keyOption, index) in countByKeyOptions"
              :key="keyOption.id"
              show-remove
              :removable="countByKeyOptions.length > 1"
              @remove="removeKey(index)"
              v-model:option="countByKeyOptions[index].value"
              @focusin="countByFieldActive = true"
              @focusout="countByFieldActive = false"
              :disabled="readOnly"
            />
            <div
              v-if="!isCountByValid.valid"
              class="key-invalid rbz-text-medium rbz-text-danger mb-2"
            >
              {{ $t(isCountByValid.messageKey) }}
            </div>
            <rbz-form-button
              :label="$t('flowControlPolicy.configPage.countByNewEntry')"
              class="new-count-by-button"
              data-qa="add-count-by-button"
              variant="secondary"
              @button-click="addKey()"
              :disabled="readOnly"
            />
          </div>
          <tags-selection-table
            class="include-exclude-table"
            :columns="tagsSelectionTableColumns"
            @update:tags="
              selectedFlowControlPolicyData[$event.fieldName as IncludeExcludeType] = $event.tags
            "
            :disabled="readOnly"
          />
        </div>
        <div class="rbz-column is-8">
          <div class="steps-wrapper">
            <template v-for="(step, stepsIndex) in steps" :key="step.id">
              <div class="rbz-card white step-card">
                <div
                  class="step-entry method-step-entry"
                  :class="{ 'is-active': activeMethodDropdownIndex === stepsIndex }"
                >
                  <label class="rbz-label rbz-label-inline width-140px">
                    {{ $t('flowControlPolicy.configPage.steps.method') }}
                  </label>
                  <div>
                    <rbz-dropdown
                      v-if="HTTP_REQUEST_METHODS.length"
                      :items="HTTP_REQUEST_METHODS.map((item) => [item, item] as [string, string])"
                      class="method-dropdown"
                      data-qa="method-dropdown"
                      placeholder="Method"
                      v-model:value="step.value.method"
                      @is-active="setActiveMethodDropdownIndex(stepsIndex, $event)"
                      :disabled="readOnly"
                    />
                  </div>
                  <div class="width-80px"></div>
                </div>
                <layout-divider-line color="light-gray" />
                <div
                  class="step-entry host-step-entry"
                  :class="{ 'is-active': activeHostInputIndex === stepsIndex }"
                >
                  <label class="rbz-label rbz-label-inline width-140px">
                    {{ $t('flowControlPolicy.configPage.steps.host') }}
                  </label>
                  <rbz-form-input
                    name="host"
                    class="host-entry-input"
                    data-qa="host-input"
                    icon="IconCode"
                    icon-location="left"
                    @focus="setActiveHostIndex(stepsIndex)"
                    @blur="setActiveHostIndex(null)"
                    v-model:inputValue="step.value.host"
                    :disabled="readOnly"
                    :is-invalid="!isValidStepFieldValue(step.value.host)"
                  />
                  <div class="width-80px"></div>
                </div>
                <layout-divider-line color="light-gray" />
                <div
                  class="step-entry path-step-entry"
                  :class="{ 'is-active': activePostInputIndex === stepsIndex }"
                >
                  <label class="rbz-label rbz-label-inline width-140px">
                    {{ $t('flowControlPolicy.configPage.steps.path') }}
                  </label>
                  <rbz-form-input
                    name="path"
                    class="path-entry-input"
                    data-qa="path-input"
                    @focus="setActivePathIndex(stepsIndex)"
                    @blur="setActivePathIndex(null)"
                    v-model:inputValue="step.value.uri"
                    :disabled="readOnly"
                    :is-invalid="!isValidStepFieldValue(step.value.uri)"
                  />
                  <div class="width-80px"></div>
                </div>
                <layout-divider-line color="light-gray" />
                <template
                  v-for="(stepEntry, stepEntryIndex) in step.value.entries"
                  :key="stepEntry.id"
                >
                  <div class="step-entry">
                    <label class="rbz-label rbz-label-inline width-140px">
                      {{ getListEntryTitle(stepEntry.value[0]) }}
                    </label>
                    <div
                      class="step-entry-key ellipsis"
                      :ref="(el) => (stepEntryKeyRefs[stepEntry.id] = el as HTMLDivElement)"
                      v-b-tooltip="
                        Utils.elementTreeOverflow(stepEntryKeyRefs[stepEntry.id])
                          ? stepEntry.value[1][0]
                          : ''
                      "
                    >
                      {{ stepEntry.value[1][0] }}
                    </div>
                    <div
                      class="ellipsis"
                      :ref="(el) => (stepEntryValueRefs[stepEntry.id] = el as HTMLDivElement)"
                      v-b-tooltip="
                        Utils.elementTreeOverflow(stepEntryValueRefs[stepEntry.id])
                          ? stepEntry.value[1][1]
                          : ''
                      "
                    >
                      {{ stepEntry.value[1][1] }}
                    </div>
                    <div class="width-80px">
                      <rbz-form-button
                        variant="tertiary"
                        icon="IconDelete"
                        class="remove-entry-button"
                        data-qa="remove-entry-button"
                        v-b-tooltip="$t('flowControlPolicy.configPage.steps.removeEntryTooltip')"
                        @button-click="removeStepEntry(stepsIndex, stepEntryIndex)"
                      />
                    </div>
                  </div>
                  <layout-divider-line color="light-gray" />
                </template>
                <div class="step-entry new-entry-row" v-if="newStepIndex === stepsIndex">
                  <div class="width-140px">
                    <rbz-dropdown
                      :items="
                        Object.keys(listEntryTypes).map((key) => {
                          return [key, listEntryTypes[key as ArgsCookiesHeadersType]] as [
                            string,
                            string,
                          ];
                        })
                      "
                      classes="new-entry-type-selection"
                      ref="newStepEntryDropdown"
                      v-model:value="newStepEntryType"
                    />
                  </div>
                  <div class="width-200px">
                    <rbz-form-input
                      name="name"
                      classes="new-entry-name-input"
                      :placeholder="$t('flowControlPolicy.configPage.steps.newEntryName')"
                      data-qa="new-entry-name-input"
                      icon="IconCode"
                      icon-location="left"
                      v-model:inputValue="newStepEntryItem.name"
                    />
                  </div>
                  <div>
                    <rbz-form-input
                      name="value"
                      classes="new-entry-value-input"
                      data-qa="new-entry-value-input"
                      :placeholder="$t('flowControlPolicy.configPage.steps.newEntryValue')"
                      icon="IconCode"
                      icon-location="left"
                      v-model:inputValue="newStepEntryItem.value"
                    />
                  </div>
                  <div class="width-80px">
                    <form-confirmation
                      @confirm="addStepEntry(stepsIndex)"
                      :is-invalid="stepEntryInvalid(stepsIndex)"
                      @cancel="setNewStepEntryIndex(-1)"
                    />
                  </div>
                </div>
                <div class="step-entry-buttons" v-if="newStepIndex !== stepsIndex">
                  <rbz-form-button
                    class="add-entry-button"
                    data-qa="add-entry-button"
                    variant="tertiary"
                    v-b-tooltip="
                      readOnly ? '' : $t('flowControlPolicy.configPage.steps.addEntryTooltip')
                    "
                    icon="IconAdd"
                    @button-click="setNewStepEntryIndex(stepsIndex)"
                    :disabled="readOnly"
                  />
                  <rbz-form-button
                    class="remove-step-button"
                    data-qa="remove-step-button"
                    variant="tertiary"
                    v-b-tooltip="
                      readOnly ? '' : $t('flowControlPolicy.configPage.steps.removeStepTooltip')
                    "
                    icon="IconDelete"
                    @button-click="removeStep(stepsIndex)"
                    :disabled="readOnly"
                  />
                </div>
              </div>
              <div class="then-divider" v-if="stepsIndex !== steps.length - 1">
                <layout-divider-line color="gray" />
                <rbz-custom-tag
                  :value="$t('flowControlPolicy.configPage.steps.then')"
                  hide-tooltip
                  variant="outline"
                  :click-enabled="false"
                  :double-click-enabled="false"
                  class="then-label"
                />
              </div>
            </template>
            <div
              v-if="!stepsValidation.valid && stepsValidation.messageKey !== ''"
              class="rbz-text-medium rbz-text-danger mb-2"
            >
              {{ $t(stepsValidation.messageKey) }}
            </div>
            <rbz-form-button
              :label="$t('flowControlPolicy.configPage.steps.addStep')"
              class="add-step-button"
              data-qa="add-step-button"
              variant="secondary"
              @button-click="addStep()"
              :disabled="readOnly"
            />
          </div>
        </div>
      </div>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadFlowControlPolicy(selectedFlowControlPolicy);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteFlowControlPolicyModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteFlowControlPolicy"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteFlowControlPolicyModal = false"
        @closeModal="showDeleteFlowControlPolicyModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', {
              name: selectedFlowControlPolicyData.name,
            })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, nextTick } from 'vue';
import {
  ArgsCookiesHeadersType,
  BasicDocument,
  DocumentName,
  FlowControlPolicy,
  FlowControlPolicyStep,
  HttpRequestMethods,
  KeyOptionData,
  KeyOptionType,
  TagsSelectionTableColumn,
  IncludeExcludeType,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';

import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import KeyOption from '@/components/KeyOption.vue';
import { HTTP_REQUEST_METHODS } from '@/types/const';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import GitHistory from '@/components/GitHistory.vue';
import TagsSelectionTable from '@/components/TagsSelectionTable.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { useTagsStore } from '@/store/TagsStore';
import { useRouting } from '@/helpers/RoutingUtils';

type Step = {
  id: string;
  value: {
    method: HttpRequestMethods;
    host: string;
    uri: string;
    entries: {
      id: string;
      value: [ArgsCookiesHeadersType, string[]];
    }[];
  };
};

export default defineComponent({
  name: 'FlowControlPoliciesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    TagsSelectionTable,
    RbzCustomTag,
    RbzFormButton,
    FormConfirmation,
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    GitHistory,
    KeyOption,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    TagsSelection,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      selectedFlowControlPolicy: null as string,
      selectedFlowControlPolicyData: null as FlowControlPolicy,
      originalFlowControlPolicyData: null as FlowControlPolicy,
      countByKeyOptions: null as KeyOptionData[],
      steps: null as Step[],
      pageLoadingCounter: 0,
      isDeleteLoading: false,
      showDeleteFlowControlPolicyModal: false,

      HTTP_REQUEST_METHODS,
      listEntryTypes: {
        headers: this.$t('flowControlPolicy.configPage.steps.header'),
        args: this.$t('flowControlPolicy.configPage.steps.argument'),
        cookies: this.$t('flowControlPolicy.configPage.steps.cookie'),
      },
      defaultStep: { ...DatasetsUtils.defaultFlowControlStep },
      newStepIndex: -1,
      newStepEntryType: 'args' as ArgsCookiesHeadersType,
      newStepEntryItem: {
        name: '',
        value: '',
      },
      activeMethodDropdownIndex: null as number | null,
      activeHostInputIndex: null as number | null,
      activePostInputIndex: null as number | null,
      countByFieldActive: false,
      isNew: false,

      stepEntryKeyRefs: {} as { [key: string]: HTMLDivElement },
      stepEntryValueRefs: {} as { [key: string]: HTMLDivElement },
    };
  },
  watch: {
    countByKeyOptions: {
      handler: function () {
        if (this.selectedFlowControlPolicyData) {
          this.selectedFlowControlPolicyData.key = _.map(this.countByKeyOptions, 'value');
        }
      },
      deep: true,
    },
    steps: {
      handler: function () {
        if (this.selectedFlowControlPolicyData) {
          this.selectedFlowControlPolicyData.steps = _.map(this.steps, (step) => {
            const args: FlowControlPolicyStep['args'] = {};
            _.forEach(
              _.filter(step.value.entries, (entry) => {
                return entry.value[0] === 'args';
              }),
              (entry) => {
                args[entry.value[1][0]] = entry.value[1][1];
              }
            );
            const cookies: FlowControlPolicyStep['cookies'] = {};
            _.forEach(
              _.filter(step.value.entries, (entry) => {
                return entry.value[0] === 'cookies';
              }),
              (entry) => {
                cookies[entry.value[1][0]] = entry.value[1][1];
              }
            );
            const headers: FlowControlPolicyStep['headers'] = {};
            _.forEach(
              _.filter(step.value.entries, (entry) => {
                return entry.value[0] === 'headers';
              }),
              (entry) => {
                headers[entry.value[1][0]] = entry.value[1][1];
              }
            );
            headers['host'] = step.value.host;
            const stepsObject: FlowControlPolicyStep = {
              args: args,
              cookies: cookies,
              headers: headers,
              method: step.value.method,
              uri: step.value.uri,
            };
            return stepsObject;
          });
        }
      },
      deep: true,
    },
  },
  computed: {
    Utils() {
      return Utils;
    },
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('flowControlPolicy.plural'),
        singular: this.$t('flowControlPolicy.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/flow-control-policies/versions`;
    },

    isLoading(): boolean {
      return this.pageLoadingCounter > 0 || this.isDeleteLoading;
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedFlowControlPolicyData, this.originalFlowControlPolicyData);
    },

    selectedFlowControlPolicyProtected(): boolean {
      return this.selectedFlowControlPolicy?.startsWith('__');
    },

    deleteTooltip(): string {
      if (this.selectedFlowControlPolicyProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      return '';
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    automaticTags(): string[] {
      const name = this.tagsStore.textToTag(this.selectedFlowControlPolicyData.name);
      const nameTag = `fc-name:${name}`;
      return [nameTag];
    },

    tagsSelectionTableColumns(): TagsSelectionTableColumn[] {
      return [
        {
          title: this.$t('common.include'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'include',
              tags: this.selectedFlowControlPolicyData['include'],
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-6',
        },
        {
          title: this.$t('common.exclude'),
          lists: [
            {
              title: this.$t('common.apply'),
              fieldName: 'exclude',
              tags: this.selectedFlowControlPolicyData['exclude'],
              tagVariant: (tag: string) => {
                if (this.duplicateTags.includes(tag)) {
                  return 'error';
                }
                return 'fill';
              },
            },
          ],
          classes: 'is-6',
        },
      ];
    },

    duplicateTags(): string[] {
      const allTags = _.concat(
        this.selectedFlowControlPolicyData?.['include'] ?? [],
        this.selectedFlowControlPolicyData?.['exclude'] ?? []
      );
      return _.filter(allTags, (val, i, iteratee) => _.includes(iteratee, val, i + 1));
    },

    isCountByValid(): { valid: boolean; messageKey: string } {
      let isCountByValid;
      let messageKey;
      const keysToCheck = _.countBy(this.selectedFlowControlPolicyData?.key, (item) => {
        if (!item) {
          return '';
        }
        const key = Object.keys(item)[0];
        return `${key}_${item[key]}`;
      });

      // get values, remove default prefix jwt.
      const values = this.selectedFlowControlPolicyData?.key.map((c) => {
        if (!c) {
          return '';
        }
        const rawValue = String(Object.values(c)?.[0]);
        return Object.keys(c)?.[0] === 'plugins' && rawValue.startsWith('jwt.')
          ? rawValue.slice(4)
          : rawValue;
      });

      messageKey = '';
      isCountByValid = true;

      for (const key of Object.keys(keysToCheck)) {
        if (keysToCheck[key] > 1 || keysToCheck[''] > 0) {
          messageKey = 'flowControlPolicy.configPage.countByInvalidMustBeUnique';
          isCountByValid = false;
          break;
        }
      }

      // only check values if no error was found before
      if (isCountByValid && values) {
        for (const value of values) {
          if (this.stringHasWhitespaces(value)) {
            messageKey = 'flowControlPolicy.configPage.countByInvalidSpaces';
            isCountByValid = false;
            break;
          }
          if (value.trim() === '') {
            messageKey = 'flowControlPolicy.configPage.countByInvalidEmpty';
            isCountByValid = false;
            break;
          }
        }
      }

      return { valid: isCountByValid, messageKey: messageKey };
    },

    isTimeframeValid(): boolean {
      const timeframe = this.selectedFlowControlPolicyData?.timeframe ?? 0;
      return timeframe > 0;
    },

    stepsValidation(): { valid: boolean; messageKey: string } {
      if (this.steps === null) {
        return { valid: false, messageKey: '' };
      }
      for (const step of this.steps) {
        if (!this.isValidStep(step)) {
          return {
            valid: false,
            messageKey: 'flowControlPolicy.configPage.steps.invalid',
          };
        }
      }
      return { valid: true, messageKey: '' };
    },

    isFlowControlPolicyValid(): boolean {
      const noDuplicates = this.duplicateTags.length === 0;
      const trimmedName = (this.selectedFlowControlPolicyData?.name ?? '').toLowerCase().trim();
      const nameExists = _.filter(this.dropdownData, (item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedFlowControlPolicyData?.name &&
        (trimmedName === (this.originalFlowControlPolicyData?.name ?? '').toLowerCase().trim() ||
          !nameExists);

      const stepsValid = this.stepsValidation.valid;

      return (
        noDuplicates &&
        this.isCountByValid.valid &&
        nameValid &&
        this.isTimeframeValid &&
        stepsValid
      );
    },

    saveTooltip(): string {
      if (this.isLoading) {
        return '';
      }
      if (!this.isTimeframeValid) {
        return this.$t('rateLimitRule.configPage.timeFrameInvalid');
      }
      return '';
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('flow-control-policies'));
    },

    switchFlowControlPolicy(flowControlPolicyId: string) {
      this.navigateTo(this.getEditPath('flow-control-policies', flowControlPolicyId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/flow-control-policies`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    async loadFlowControlPolicy(flowControlPolicyId: string) {
      this.setPageLoadingStatus(true);
      this.selectedFlowControlPolicy = flowControlPolicyId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/flow-control-policies/${flowControlPolicyId}`,
      });
      if (!response?.data || _.isEmpty(response.data)) {
        this.redirectToList();
        return;
      }
      this.selectedFlowControlPolicyData = response.data;
      this.originalFlowControlPolicyData = _.cloneDeep(this.selectedFlowControlPolicyData);
      this.reloadCountByKeyOptions();
      this.reloadSteps();
      this.setPageLoadingStatus(false);
    },

    async duplicateFlowControlPolicy() {
      this.setPageLoadingStatus(true);
      const flowControlPolicyToAdd = _.cloneDeep(this.selectedFlowControlPolicyData);
      flowControlPolicyToAdd.id = DatasetsUtils.generateUUID2();
      flowControlPolicyToAdd.name = Utils.generateUniqueEntityName(
        flowControlPolicyToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/flow-control-policies/${flowControlPolicyToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: flowControlPolicyToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchFlowControlPolicy(flowControlPolicyToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadFlowControlPolicy() {
      Utils.downloadFile('flow-control-policy', 'json', this.selectedFlowControlPolicyData);
    },

    openDeleteFlowControlPolicyModal() {
      this.showDeleteFlowControlPolicyModal = true;
    },

    async deleteFlowControlPolicy() {
      this.isDeleteLoading = true;
      const flowControlPolicyId = this.selectedFlowControlPolicy;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === flowControlPolicyId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedFlowControlPolicy = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/flow-control-policies/${flowControlPolicyId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedFlowControlPolicyData.name = this.selectedFlowControlPolicyData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/flow-control-policies/${this.selectedFlowControlPolicyData.id}`,
        data: this.selectedFlowControlPolicyData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedFlowControlPolicyData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalFlowControlPolicyData = _.cloneDeep(this.selectedFlowControlPolicyData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    reloadCountByKeyOptions() {
      this.countByKeyOptions = _.map(this.selectedFlowControlPolicyData.key, (keyValue) => {
        return {
          id: DatasetsUtils.generateUUID2(),
          value: keyValue,
        };
      });
    },

    reloadSteps() {
      this.steps = _.map(this.selectedFlowControlPolicyData.steps, (step) => {
        return this.addIdsToStep(step);
      });
    },

    getOptionTextKey(option: KeyOptionType, index: number) {
      if (!option) {
        return '';
      }
      const [type] = Object.keys(option);
      return `${this.selectedFlowControlPolicyData.id}_${type}_${index}`;
    },

    addKey() {
      this.countByKeyOptions.push({
        id: DatasetsUtils.generateUUID2(),
        value: {
          attrs: 'ip',
        },
      });
    },

    removeKey(index: number) {
      if (this.countByKeyOptions.length > 1) {
        this.countByKeyOptions.splice(index, 1);
      }
    },

    addIdsToStep(step: FlowControlPolicyStep): Step {
      const mergedEntries = this.stepEntries(step);
      const entriesWithIds = _.map(mergedEntries, (entry) => {
        return {
          id: DatasetsUtils.generateUUID2(),
          value: entry,
        };
      });
      return {
        id: DatasetsUtils.generateUUID2(),
        value: {
          method: step.method,
          uri: step.uri,
          host: step.headers.host,
          entries: entriesWithIds,
        },
      };
    },

    stepEntries(step: FlowControlPolicyStep): [ArgsCookiesHeadersType, string[]][] {
      const headersEntries = Object.entries(step.headers) as string[][];
      const cookiesEntries = Object.entries(step.cookies) as string[][];
      const argsEntries = Object.entries(step.args) as string[][];
      const mergedEntries = [];
      for (let i = 0; i < headersEntries.length; i++) {
        if (headersEntries[i][0] !== 'host') {
          mergedEntries.push(['headers', headersEntries[i]] as [ArgsCookiesHeadersType, string[]]);
        }
      }
      for (let i = 0; i < argsEntries.length; i++) {
        mergedEntries.push(['args', argsEntries[i]] as [ArgsCookiesHeadersType, string[]]);
      }
      for (let i = 0; i < cookiesEntries.length; i++) {
        mergedEntries.push(['cookies', cookiesEntries[i]] as [ArgsCookiesHeadersType, string[]]);
      }
      return mergedEntries;
    },

    addStep() {
      const newStep = _.cloneDeep(this.defaultStep);
      if (this.steps.length > 0) {
        const firstStep = this.steps[0].value;
        newStep.headers.host = firstStep.host;
      }
      this.steps.push(this.addIdsToStep(newStep));
    },

    removeStep(stepsIndex: number) {
      this.steps.splice(stepsIndex, 1);
    },

    addStepEntry(stepsIndex: number) {
      const step = this.steps[stepsIndex].value;
      const newEntryName = this.newStepEntryItem.name.trim().toLowerCase();
      const newEntryValue = this.newStepEntryItem.value.trim().toLowerCase();
      if (newEntryName && newEntryValue && !this.stepEntryInvalid(stepsIndex)) {
        step.entries.push({
          id: DatasetsUtils.generateUUID2(),
          value: [this.newStepEntryType, [newEntryName, newEntryValue]],
        });
      }
      this.setNewStepEntryIndex(-1);
    },

    stepEntryInvalid(stepsIndex: number) {
      const newEntryName = this.newStepEntryItem.name.trim().toLowerCase();
      const existingKeys =
        this.selectedFlowControlPolicyData.steps[stepsIndex][this.newStepEntryType];
      const headerHost = newEntryName === 'host' && this.newStepEntryType === 'headers';
      return newEntryName in existingKeys || headerHost;
    },

    removeStepEntry(stepIndex: number, stepEntryIndex: number) {
      const step = this.steps[stepIndex].value.entries;
      step.splice(stepEntryIndex, 1);
    },

    setNewStepEntryIndex(index: number) {
      this.newStepEntryItem = {
        name: '',
        value: '',
      };
      this.newStepEntryType = 'args';
      this.newStepIndex = index;
      if (index !== -1) {
        nextTick(() => {
          const rawRef = this.$refs.newStepEntryDropdown as
            | { focusDropdown?: () => void }
            | ({ focusDropdown?: () => void } | undefined)[]
            | undefined;
          const dropdown = Array.isArray(rawRef) ? rawRef[index] : rawRef;
          dropdown?.focusDropdown?.();
        });
      }
    },

    getListEntryTitle(seqEntry: ArgsCookiesHeadersType): string {
      return this.listEntryTypes[seqEntry];
    },

    setActiveMethodDropdownIndex(index: number, value: boolean) {
      if (!value && this.activeMethodDropdownIndex === index) {
        this.activeMethodDropdownIndex = null;
      }
      if (value) {
        this.activeMethodDropdownIndex = index;
      }
    },

    setActiveHostIndex(index: number) {
      this.activeHostInputIndex = index;
    },

    setActivePathIndex(index: number) {
      this.activePostInputIndex = index;
    },

    newFlowControlPolicy(): FlowControlPolicy {
      const factory = DatasetsUtils.newEntityFactory['flow-control-policies'];
      return factory && factory();
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedFlowControlPolicyData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData as FlowControlPolicy, Boolean(entityNotSaved));
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
    timeFrameError() {
      if (!this.isTimeframeValid) {
        return this.$t('flowControlPolicy.configPage.timeFrameInvalid');
      }
      return '';
    },
    isValidStepFieldValue(value: string): boolean {
      if (value === '' || this.stringHasWhitespaces(value)) return false;
      return true;
    },
    isValidStep(step: Step): boolean {
      if (!this.isValidStepFieldValue(step.value.host)) return false;
      if (!this.isValidStepFieldValue(step.value.uri)) return false;

      return true;
    },
    stringHasWhitespaces(value: string): boolean {
      const regex = /\s/; // whitespaces
      return regex.test(value);
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'flow-control-policies') {
          return;
        }
        this.setPageLoadingStatus(true);
        await this.branchesStore.getBranches();
        await this.loadFlowControlPolicy(docId);
        this.setPageLoadingStatus(false);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const rawName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Flow Control Policy', 'Flow Control Policies Editor'].includes(rawName)) {
          this.setPageLoadingStatus(true);
          this.isNew = rawName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedFlowControlPolicyData = this.newFlowControlPolicy();
            this.selectedFlowControlPolicyData.name = Utils.generateUniqueEntityName(
              this.selectedFlowControlPolicyData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalFlowControlPolicyData = _.cloneDeep(this.selectedFlowControlPolicyData);
            this.reloadCountByKeyOptions();
            this.reloadSteps();
          } else {
            const flowControlPolicyId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === flowControlPolicyId;
            });
            if (dataExists) {
              await this.loadFlowControlPolicy(flowControlPolicyId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedFlowControlPolicyData = JSON.parse(savedSessionData);
            this.reloadCountByKeyOptions();
            this.reloadSteps();
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.step-entry {
  color: map.get(colors.$colors, 'shuttle-gray');
}

.step-entry,
.step-entry-buttons {
  align-items: center;
  display: flex;
}

.step-entry > * {
  flex-grow: 1;
  padding: 4px 8px;
}

.step-entry-key {
  max-width: min-content;
}

.remove-entry-button {
  margin-left: auto;
}

.then-divider {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
  width: 100%;

  .then-label {
    background-color: map.get(colors.$colors, 'white');
    position: absolute;
  }
}
</style>
